import type { IntelligenceAttestationView } from "@exploitlens/ui-contracts";

/**
 * Renders a real, persisted Telegraph attestation -- clearly separated
 * from the deterministic verdict above it. This section's own framing is
 * load-bearing, not decoration: nothing here may be read as evidence
 * ExploitLens used to reach a verdict, because it isn't -- see
 * `docs/PRD.md` section 2's isolation guarantee. Only `attestation.disclaimer`
 * (the real, schema-carried disclaimer string) and the `normalized` fields
 * are rendered, per `IntelligenceAttestationView`'s own doc comment about
 * never rendering the raw miner payload.
 */
export function IntelligenceAttestation({
  attestation,
}: {
  attestation: IntelligenceAttestationView;
}) {
  return (
    <div className="rounded border border-rule bg-canvas-raised p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-ink">Telegraph transaction intelligence</h3>
        <span className="rounded-full bg-canvas px-2 py-0.5 text-xs uppercase tracking-wide text-ink-faint">
          Not part of the verdict
        </span>
      </div>
      <p className="mt-2 text-sm text-ink-muted">{attestation.disclaimer}</p>
      <p className="mt-3 font-evidence text-xs text-ink-faint">
        Anchored to bundle digest {attestation.bundleDigest.slice(0, 18)} recorded{" "}
        {attestation.generatedAt}
      </p>

      <ul className="mt-4 space-y-3">
        {attestation.signals.map((signal, i) => (
          <li key={i} className="rounded border border-rule bg-canvas px-3 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="text-sm text-ink">
                {signal.minerName}{" "}
                <span className="font-evidence text-xs text-ink-faint">(id {signal.minerId})</span>
              </span>
              <span
                className={[
                  "text-xs font-medium",
                  signal.paymentStatus === "settled" ? "text-preserved" : "text-ink-muted",
                ].join(" ")}
              >
                {signal.paymentStatus === "settled" ? "Payment settled" : "No payment required"}
              </span>
            </div>
            {signal.summary && <p className="mt-2 text-sm text-ink">{signal.summary}</p>}
            <p className="mt-2 font-evidence text-xs text-ink-faint">
              {signal.intent} {signal.network} signal {signal.signalHash.slice(0, 14)}{" "}
              {signal.requestedAt}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
